import {TaskType} from "../../../../Types/Task";
import {StatusEnum} from "../../../../Types/StatusEnum";
import {getTaskLeafIds, pruneToC, extractToC, mergeToC} from "./tocTreeUtils";
import moment from "moment";

export interface SelectionClassification {
    strategy: "legacy" | "new";
    affectedTasks: TaskType[];
    fullySelectedTasks: TaskType[];
    partiallySelectedTasks: TaskType[];
}

/**
 * Classify the user's checkbox selection to determine which update strategy to use.
 *
 * Legacy: exactly 1 task affected AND all of its subs are selected.
 * New:    everything else (partial, cross-task, etc.)
 */
export function classifySelection(
    checkedIds: string[],
    relatedTasks: TaskType[]
): SelectionClassification {
    const checkedSet = new Set(checkedIds);

    const affectedTasks: TaskType[] = [];
    const fullySelectedTasks: TaskType[] = [];
    const partiallySelectedTasks: TaskType[] = [];

    for (const task of relatedTasks) {
        const leafIds = getTaskLeafIds(task);
        const selectedLeaves = leafIds.filter((id) => checkedSet.has(id));

        if (selectedLeaves.length === 0) continue;

        affectedTasks.push(task);

        if (selectedLeaves.length === leafIds.length) {
            fullySelectedTasks.push(task);
        } else {
            partiallySelectedTasks.push(task);
        }
    }

    const isLegacy =
        affectedTasks.length === 1 &&
        fullySelectedTasks.length === 1 &&
        partiallySelectedTasks.length === 0;

    return {
        strategy: isLegacy ? "legacy" : "new",
        affectedTasks,
        fullySelectedTasks,
        partiallySelectedTasks,
    };
}

interface WorkItemResponse {
    WorkItemId: string;
    LinkToDocument?: string;
    SharepointPath?: string;
    SharepointFolder?: string;
}

type TaskUpdater = (prevTasks: TaskType[]) => TaskType[];

interface UpdateResult {
    updater: TaskUpdater;
}

/**
 * Build the field overrides common to both strategies.
 */
function buildStatusFields(isFastRevision: boolean, wi: WorkItemResponse) {
    return {
        Status: isFastRevision ? StatusEnum.readyForReview : StatusEnum.init,
        WorkItemStatus: isFastRevision ? StatusEnum.readyForReview : StatusEnum.init,
        DueDate: isFastRevision ? "" : moment.utc().add(1, "month").format(),
        Description: isFastRevision
            ? "Vent på innspill fra fagredaktør og korrekturleser"
            : "Må gjennomgås",
        WorkItemId: wi.WorkItemId,
        ...(wi.LinkToDocument && {LinkToDocument: wi.LinkToDocument}),
        ...(wi.SharepointPath && {SharepointPath: wi.SharepointPath}),
        ...(wi.SharepointFolder && {SharepointFolder: wi.SharepointFolder}),
        AuthorCreatedWI: true,
    };
}

/**
 * Legacy strategy: update the single affected task in-place.
 * Identical to the original RevisionModal/ManuscriptModal behavior.
 */
export function applyLegacyUpdate(
    affectedTask: TaskType,
    wi: WorkItemResponse,
    isFastRevision: boolean
): UpdateResult {
    const overrides = buildStatusFields(isFastRevision, wi);

    const updater: TaskUpdater = (prevTasks) =>
        prevTasks.map((t) =>
            t.TaskId === affectedTask.TaskId ? {...t, ...overrides} : t
        );

    return {updater};
}

/**
 * New strategy: create a new task, prune/remove affected source tasks.
 *
 * - Fully-selected tasks are removed from the list.
 * - Partially-selected tasks have selected subs pruned from ToC and PartIds.
 * - A new task is inserted with the combined selected subs.
 */
export function applyNewUpdate(
    triggerTask: TaskType,
    checkedIds: string[],
    classification: SelectionClassification,
    wi: WorkItemResponse,
    isFastRevision: boolean
): UpdateResult {
    const checkedSet = new Set(checkedIds);
    const fullySelectedIds = new Set(classification.fullySelectedTasks.map((t) => t.TaskId));
    const partiallySelectedIds = new Set(classification.partiallySelectedTasks.map((t) => t.TaskId));

    // Build ToC for the new task by extracting selected leaves from each affected task
    const extractedTrees = classification.affectedTasks
        .map((task) => task.ToC ? extractToC(task.ToC, checkedSet) : null);
    const mergedToC = mergeToC(extractedTrees);

    // Build the new task
    const overrides = buildStatusFields(isFastRevision, wi);
    const newTask: TaskType = {
        ...triggerTask,
        ...overrides,
        TaskId: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        PartIds: checkedIds,
        PartTitles: [], // Will be populated on next refresh
        ToC: mergedToC!,
    };

    const updater: TaskUpdater = (prevTasks) => {
        const result: TaskType[] = [];

        for (const task of prevTasks) {
            if (fullySelectedIds.has(task.TaskId)) {
                // Fully selected — remove from list
                continue;
            }

            if (partiallySelectedIds.has(task.TaskId)) {
                // Partially selected — prune selected subs
                const prunedToC = task.ToC ? pruneToC(task.ToC, checkedSet) : null;
                const taskLeafIds = getTaskLeafIds(task);
                const remainingPartIds = task.PartIds.filter((id) => !checkedSet.has(id));

                if (!prunedToC || remainingPartIds.length === 0) {
                    // Edge case: pruning removed everything — remove task
                    continue;
                }

                result.push({
                    ...task,
                    ToC: prunedToC,
                    PartIds: remainingPartIds,
                    PartTitles: task.PartTitles.filter((_, i) =>
                        i < taskLeafIds.length && !checkedSet.has(taskLeafIds[i])
                    ),
                });
            } else {
                // Unaffected task — keep as-is
                result.push(task);
            }
        }

        // Add the new task
        result.push(newTask);
        return result;
    };

    return {updater};
}